import { Keyboard, Platform, TouchableOpacity, View } from "react-native"
import CurrencyInput, { formatNumber } from "react-native-currency-input"
// import formatNumber from "react-native-currency-input"
import Tooltip from "rn-tooltip"
import InfoIcon from "@/assets/icons/tooltip-i-notfilled.svg"
import { BFColors } from "@/constants/Colors"
import { type ReactNode } from "react"
import BFText, { Body, H4, Small } from "@/components/standard/BFText"
import { BFSimpleTextInput } from "@/components/standard/BFTextInput"
import { cn } from "@/components/util/cn"

interface CalculatorItemProps {
  title: string
  subtitle?: string | null
  value?: number | null
  range?: number[]
  prefix?: string
  suffix?: string
  precision?: number | null
  noMin?: boolean
  textColor?: string
  tooltip?: string | ReactNode
  blankText?: string
  onValueChange?: (val: number) => void
  onBlur?: () => void
  onBlankPress?: () => void
  isError?: boolean
  blurb?: string | null
  variant?: CalculatorItemVariant
  testId?: string
}

const CalculatorItemVariants = {
  default: {
    containerClassName: "",
    inputClassName:
      "w-[115px] border border-light-grey rounded-none font-figtree-bold text-calm-blue text-right text-[16px] p-2",
    titleComponent: Body,
  },
  modal: {
    containerClassName: "pr-2",
    inputClassName:
      "max-w-[120px] border border-light-grey rounded-none flex-1 text-right text-[16px] text-right bg-lightest font-figtree-bold px-3 py-2",
    titleComponent: H4,
  },
  tight: {
    containerClassName: "",
    inputClassName:
      "w-[115px] border border-light-grey rounded-none font-figtree-bold text-calm-blue text-right text-[16px] p-2",
    titleComponent: Body,
  },
}

type CalculatorItemVariant = keyof typeof CalculatorItemVariants

export default function CalculatorItem({
  title,
  subtitle,
  value,
  range,
  prefix = "$",
  precision,
  noMin,
  suffix,
  textColor,
  tooltip,
  blankText,
  onValueChange,
  onBlur,
  onBlankPress,
  isError,
  testId,
  blurb,
  variant = "default",
}: CalculatorItemProps) {
  const handleChangeValue = (val: number) => {
    if (onValueChange) {
      onValueChange(val)
    }
  }

  const displayNumber = (v: number | null | undefined) =>
    formatNumber(v || 0, {
      precision: precision || undefined,
      suffix,
      prefix,
      delimiter: ",",
      separator: ".",
    })

  const {
    containerClassName,
    inputClassName,
    titleComponent: TitleComponent,
  } = CalculatorItemVariants[variant]

  const isEditable = !!(onValueChange || onBlur)

  function renderInput() {
    if (isEditable) {
      return (
        <CurrencyInput
          value={value || 0}
          onChangeValue={handleChangeValue}
          onBlur={onBlur}
          // onFocus={handleFocus(value)}
          prefix={prefix}
          // Just because this breaks Mobile Web Safari/Chrome..
          // suffix={suffix}
          delimiter=","
          separator="."
          renderTextInput={({ style, ...textInputProps }) => (
            <BFSimpleTextInput
              testID={testId}
              className={cn(inputClassName, isError && "border-red-orange")}
              style={[style, !!isError && { borderColor: BFColors.redOrange }]}
              {...textInputProps}
            />
          )}
          precision={precision || 0}
          minValue={noMin ? undefined : 0}
          signPosition="beforePrefix"
          style={{ color: textColor || BFColors.calmBlue }}
          readOnly={!onValueChange && !onBlur}
          returnKeyLabel="Done"
          returnKeyType="done"
          onSubmitEditing={Keyboard.dismiss}
          selectTextOnFocus={true}
        />
      )
    }

    return (
      <BFText
        className={cn(
          range !== undefined ? "w-[200px]" : "w-[115px]",
          "font-figtree-bold text-[16px] border text-right p-2 my-1 border-transparent text-midnight-blue"
        )}
        style={{ color: textColor || BFColors.midnightBlue }}
      >
        {range !== undefined
          ? `${displayNumber(range[0])} - ${displayNumber(range[1])}`
          : displayNumber(value)}
      </BFText>
    )
  }

  return (
    <View
      className={cn(
        "flex flex-row justify-between items-center",
        isEditable && "py-2",
        variant === "tight" && "py-1", // Reduce row padding in tight mode
        containerClassName
      )}
    >
      <View className="flex flex-col">
        <View className="flex flex-row">
          <TitleComponent className="text-midnight-blue pr-1">
            {title}
          </TitleComponent>
          {tooltip && (
            <Tooltip
              popover={
                <BFText className="font-spacegrotesk text-white text-[12px] p-[2px]">
                  {tooltip}
                </BFText>
              }
              actionType="press"
              backgroundColor={BFColors.calmBlue}
              withOverlay={false}
              height={"auto"}
              width={250}
              containerStyle={{
                borderRadius: 2,
                marginBottom: Platform.OS === "android" ? -21 : 0, // Fixes odd arrow placement that occurs only on Android
              }}
              toggleWrapperProps={{
                hitSlop: 12,
              }}
            >
              <View>
                <InfoIcon width={16} height={16} />
              </View>
            </Tooltip>
          )}
        </View>
        {!!subtitle && <Small className="text-dark-grey">{subtitle}</Small>}
      </View>
      {blurb && (
        <View className="flex mt-1 p-1 bg-neon-yellow">
          <BFText className="font-figtree-bold text-[9px] text-darkest">
            {blurb}
          </BFText>
        </View>
      )}
      {value === 0 && blankText && (
        <TouchableOpacity testID={"black-area-action"} onPress={onBlankPress}>
          <BFText className="w-[115px] h-[40px] border border-transparent text-right p-2 font-figtree-bold text-[16px] text-calm-blue underline">
            {blankText}
          </BFText>
        </TouchableOpacity>
      )}
      {(value !== 0 || !blankText) && renderInput()}
    </View>
  )
}
